/**
 * @file src/components/VidButtonBank/SupportContents/SupportContents.tsx
 * @exports SupportContents
 * @summary Form, success, and error states for the vID support slide-in panel or full-screen modal.
 */

import {
	BoxedOutlineButton,
	colors,
	Form,
	FormFieldTypeEnum,
	OutlineButton,
	PrimaryButton,
	ResizeEnum,
	SendOutline,
	Warning,
	X,
} from "@cerebruminc/cerebellum";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { cloneDeep } from "lodash";
import { FC, useMemo } from "react";
import { ThemeProvider, useTheme } from "styled-components";

import {
	CreateSupportTicketMutation,
	useCreateSupportTicketMutation,
	SupportTicketPurpose,
} from "src/generated/graphql";
import {
	currentModalStateAtom,
	highContrastThemeAtom,
	openSupportModalAtom,
	supportModalAdditionalMessagesAtom,
	SupportModalState,
} from "src/shame/shame-atoms";
import { IconBox } from "src/styles/SharedStyles";

import {
	ButtonBox,
	CloseButton,
	ContentBox,
	Description,
	FormBox,
	IconWrapper,
	Instructions,
	MessageBox,
	SlideInBox,
	Title,
} from "./SupportContentsStyles";

interface SupportContentsType {
	fullScreen?: boolean;
	height?: number;
	onClose?: () => void;
	width: number;
}

/** Renders the support form, success, and error states inside a slide-in panel or full-screen modal. */
export const SupportContents: FC<SupportContentsType> = ({ onClose, fullScreen, width, height }) => {
	const [currentState, setCurrentState] = useAtom(currentModalStateAtom);
	const setShow = useSetAtom(openSupportModalAtom);
	const [additionalMessages, setAdditionalMessages] = useAtom(supportModalAdditionalMessagesAtom);
	const isHighContrast = useAtomValue(highContrastThemeAtom);

	const parentTheme = useTheme();
	const editableTheme = useMemo(() => {
		const t = cloneDeep(parentTheme);
		t.input.asteriskColor = "transparent";
		return t;
	}, [parentTheme]);

	const isErrorState = currentState === SupportModalState.ERROR;
	const mobileMode = width < 450;

	const buttonWidth = mobileMode ? width - 60 : 330;

	const onCreateSupportTicketError = () => {
		setCurrentState(SupportModalState.ERROR);
	};

	const onCreateSupportTicketSuccess = (response: CreateSupportTicketMutation) => {
		const { success } = response.createSupportTicket;
		if (success) {
			setCurrentState(SupportModalState.SUCCESS);
			setAdditionalMessages([]);
		} else {
			onCreateSupportTicketError();
		}
	};

	const { mutate: createTicket, isLoading } = useCreateSupportTicketMutation({
		onSuccess: onCreateSupportTicketSuccess,
		onError: onCreateSupportTicketError,
	});

	const resourceCenterButton = (
		<BoxedOutlineButton
			buttonHeight={31}
			href="https://cerebrum.com/resource-center"
			target="_blank"
			text={"Resource Center"}
			useA
		/>
	);

	return (
		<SlideInBox $fullScreen={fullScreen}>
			{onClose && (
				<CloseButton onClick={onClose}>
					<IconBox>
						<X fill={colors.COOL_GREY_60} />
					</IconBox>
				</CloseButton>
			)}

			{currentState !== SupportModalState.FORM ? (
				<ContentBox $fullScreen={fullScreen}>
					<IconWrapper>
						{isErrorState ? <Warning fill={colors.RED_100} /> : <SendOutline fill={colors.BLUE_100} />}
					</IconWrapper>
					<MessageBox>
						<Title>{isErrorState ? "Oops!" : "Thank you for your submission!"}</Title>
						<Description>
							{isErrorState
								? "There was an error processing your request."
								: "You should hear back from our team in ~1 business day!"}
						</Description>
						{resourceCenterButton}
					</MessageBox>
					<ButtonBox>
						{isErrorState ? (
							<OutlineButton
								buttonWidth={buttonWidth}
								onClick={() => setCurrentState(SupportModalState.FORM)}
								text="Try Again"
							/>
						) : (
							<PrimaryButton
								buttonWidth={buttonWidth}
								onClick={() => {
									setTimeout(() => setCurrentState(SupportModalState.FORM), 400);
									setShow(false);
								}}
								text="Close"
							/>
						)}
					</ButtonBox>
				</ContentBox>
			) : (
				<ContentBox $fullScreen={fullScreen}>
					<Instructions>
						<Title>Support</Title>
						<Description>
							Let us know how we can help, or visit our Resource Center for videos, guides and FAQs.
						</Description>
						{resourceCenterButton}
					</Instructions>
					<ThemeProvider theme={editableTheme}>
						<Title>Submit a help request:</Title>
						<FormBox>
							<Form
								disableSubmitUntilValid
								enableReinitialize
								fields={[
									{
										fieldLabel: " ",
										name: "text",
										options: {
											resize: ResizeEnum.Vertical,
											placeholder: "Type your question or issue here...",
											inputHeight: height && height - 385 > 180 ? height - 385 : 180,
											failColor: colors.COOL_GREY_20,
										},
										required: true,
										requiredMessage: " ",
										type: FormFieldTypeEnum.Textarea,
									},
								]}
								rowGap={0}
								submit={(data) => {
									const message = additionalMessages
										? `${data.text}\nAdded information: ${Array.from(new Set(additionalMessages)).join(" / ")}`
										: data.text;
									createTicket({
										description: message,
										purpose: SupportTicketPurpose.TechnicalIssue,
									});
								}}
								submitButtonLoading={isLoading}
								themeOverride={{
									bottomPadding: 10,
									buttonShadow: !isHighContrast,
									submitButtonGap: 15,
									submitButtonWidth: buttonWidth,
									titleGap: 0,
									topPadding: 0,
								}}
								validateOnBlur
							/>
						</FormBox>
					</ThemeProvider>
				</ContentBox>
			)}
		</SlideInBox>
	);
};
